/**

 * T3-3.1：Studio 裁剪走 Worker（submit → poll），进度含 detectBatches / detectBatchIndex。

 * T3-3.3：poll 逻辑复用 imageCropPoll。

 */

import { mapCropRowToDetailRow } from '../utils/imageCropRowStatus';
import { collectCropOutputUrlsFromDetail } from '../utils/imageCropDetailPrints';

import { computeImageCropDetectBatches } from '../utils/imageCropStudioProgress';
import { IMAGE_CROP_WORKER_MODE } from '../constants/imageCropConfig';

import {

  fetchAllImageCropPollRows,

  runImageCropPollLoop,

} from '../utils/imageCropPoll';

import { submitImageCropTask } from './imageCropTaskApi';



function buildOutputsFromRows(items, rows, imageIds) {

  const rowMap = new Map();

  (rows || []).forEach((row) => {

    if (row?.imageId != null) rowMap.set(String(row.imageId), row);

  });



  return items.map((item, index) => {

    const row = rowMap.get(String(item.imageId));

    if (!row) {

      return {

        status: 'failed',

        index,

        sourceIndex: index,

        sourceImageId: item.imageId,

        error: '未返回裁剪结果',

      };

    }

    const detail = mapCropRowToDetailRow(item.imageId, row, item.imageUrl || '');

    const urls = collectCropOutputUrlsFromDetail(detail);

    if (detail.status === 'success' && urls.length > 0) {

      return {

        url: urls[0],

        urls,

        outputUrl: urls[0],

        imageUrl: urls[0],

        status: 'success',

        index,

        sourceIndex: index,

        sourceImageId: item.imageId,

        jobId: `${item.imageId}`,

      };

    }

    return {

      status: 'failed',

      index,

      sourceIndex: index,

      sourceImageId: item.imageId,

      error: detail.error || '裁剪失败',

    };

  });

}



/**

 * @param {object} options

 * @param {string} options.taskId

 * @param {Array<{ imageId: string, imageUrl: string }>} options.items

 * @param {() => boolean} [options.shouldAbort]

 * @param {(patch: object) => void} [options.onProgress]

 * @param {(message: string, type?: string) => void} [options.showToast]

 */

export async function runStudioImageCropBatch({

  taskId,

  items,

  shouldAbort,

  onProgress,

  showToast,

}) {

  const list = (items || []).filter((item) => item?.imageId && item?.imageUrl);

  const imageIds = list.map((item) => String(item.imageId));

  const total = list.length;

  if (total === 0) {

    return { collectedUrls: [], outputs: [], failedCount: 0, detectBatches: 0 };

  }



  const submitRes = await submitImageCropTask({

    taskId,

    images: list.map(({ imageId, imageUrl }) => ({ imageId, imageUrl })),

    mode: IMAGE_CROP_WORKER_MODE,

  }, { showToast });

  const detectBatches = Number(submitRes.detectBatches)

    || computeImageCropDetectBatches(total);



  const emitProgress = (patch = {}) => {

    onProgress?.({

      detectBatches,

      detectBatchIndex: 0,

      successCount: 0,

      failedCount: 0,

      total,

      ...patch,

    });

  };



  emitProgress();



  const pollResult = await runImageCropPollLoop({

    taskId,

    imageIds,

    detectBatches,

    shouldAbort,

    onTick: (snapshot) => {

      emitProgress({

        detectBatchIndex: snapshot.detectBatchIndex,

        successCount: snapshot.successCount,

        failedCount: snapshot.failedCount,

      });

    },

  });



  if (pollResult.aborted) {

    throw new Error('任务已终止');

  }



  const finalRows = await fetchAllImageCropPollRows(taskId, { shouldAbort });

  if (finalRows === null) {

    throw new Error('任务已终止');

  }



  const outputs = buildOutputsFromRows(list, finalRows, imageIds);

  const collectedUrls = outputs

    .filter((row) => row.status === 'success')

    .flatMap((row) => (Array.isArray(row.urls) && row.urls.length > 0 ? row.urls : [row.url]))

    .map((url) => String(url || '').trim())

    .filter(Boolean);

  const failedCount = outputs.filter((row) => row.status === 'failed').length;



  emitProgress({

    detectBatchIndex: Math.max(0, detectBatches - 1),

    successCount: collectedUrls.length,

    failedCount,

  });



  return {

    collectedUrls,

    outputs,

    failedCount,

    detectBatches,

  };

}

